import styled from "styled-components";
import Modal from "../../common/Modal";
import removeTimezone from "../../../utils/removeTimezone";
import { AiOutlineCloseCircle } from "react-icons/ai";
import ToursService from "../../../services/tours.service";
import SellerService from "../../../services/seller.service";
import { FullReservation, Group, ReservationPaymentList, TourInfo } from "../../../types/common.types";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { format } from "date-fns";
import formatNumberToString from "../../../utils/formatNumber";
import { useGlobal } from "../../../context/global";
import { useSeller } from "../../../context/seller";
import { useMemo } from "react";
import { BiListUl } from "react-icons/bi";

export default function ViewReservationData() {
    const { setMessage } = useGlobal()
    const { reservationData, setReservationData, setCreatePaymentGroupId, setCreatePaymentReservationId, setViewPaymentsData } = useSeller()
    
     function reduceReservations(reservations: FullReservation[]): FullReservation[] {
        const grouped: any = {};
        
        // Group by tour_id AND reservation_date
        reservations.forEach(reservation => {
            // Convert price and tickets to numbers for consistent calculation
            const price = Number(reservation.price) || 0;
            const tickets = Number(reservation.tickets) || 0;
            const tourId = reservation.tour_id;
            const date = reservation.reservation_date.split(/-/g).reverse().join('/'); // Extract date part only
            
            // Create a unique key combining tour_id and reservation_date
            const key = `${tourId}_${date}`;
            console.log(key)
            if (!grouped[key]) {
                grouped[key] = {
                    tour_id: tourId,
                    price: price,
                    tickets: tickets,
                    reservation_date: date
                };
            } else {
                // Sum the prices and tickets for same tour_id AND same date
                grouped[key].price += price;
                grouped[key].tickets += tickets;
            }
        });
        
        // Convert the grouped object back to array
        return Object.values(grouped);
    }

    const reducedReservations = useMemo(() => {
        if (reservationData!.type === 'group') {
            return reduceReservations(reservationData!.reservations)
        } else return []
    }, [reservationData])

    async function printBoucher() {
        
        const sellerInfo = await (await SellerService.me()).data;
        const tours: TourInfo[] = [];
        
        const newGroupReservations = reservationData!.type === 'group' ? reducedReservations : [reservationData!];
        const newGroupReservationData: Group = reservationData!.type === 'group' ? {
            id: reservationData!.group_id,
            agency_id: reservationData!.agency_id,
            created_at: reservationData!.created_at,
            reservations: reservationData!.reservations
        } : {
            id: reservationData!.id,
            agency_id: reservationData!.agency_id,
            created_at: reservationData!.created_at,
            reservations: [{
                id: reservationData!.id,
                trip_id: reservationData!.trip_id,
                agency_id: reservationData!.agency_id,
                cost: reservationData!.cost,
                reservation_date: reservationData!.reservation_date,
                tickets: reservationData!.tickets,
                price: reservationData!.price,
                payment_methods: reservationData!.payment_methods,
                passenger: reservationData!.passenger,
            } as FullReservation]
        };

        const newGroup = reservationData?.type === 'group' ? 
        reservationData!.reservations.map(r => r.passenger) 
        .filter((passenger, index, self) =>
            index === self.findIndex((p) => (
                p.id === passenger.id
            ))
        )
        : [reservationData!.passenger];
        for (let i = 0; i < newGroupReservations.length; i++) {
            const reservation = newGroupReservations[i];
            const tourInfo: TourInfo = await (await ToursService.getDetailTourInfo(reservation.tour_id, sellerInfo.agency_id)).data;
            tours.push(tourInfo);
        }
    
        const doc = new jsPDF();
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
    
        // Add fonts - with error handling
        try {
            const fontNormalUrl = '/fonts/Roboto-Regular.ttf';
            const fontMediumUrl = '/fonts/Roboto-Medium.ttf';
            const fontBoldUrl = '/fonts/Roboto-Bold.ttf';
            
            doc.addFont(fontNormalUrl, 'Roboto', 'normal');
            doc.addFont(fontMediumUrl, 'Roboto', 'medium');
            doc.addFont(fontBoldUrl, 'Roboto', 'bold');
            console.log('Fonts added');
        } catch (error) {
            console.warn('Font loading failed, using default fonts:', error);
            // Fallback to default fonts
            doc.setFont('helvetica');
        }
    
        try {
            const imageUrl = '/assets/logo.png';
            const imagePadding = 2;
            const imageSize = 30; // Square size
            const imageX = 14; // Left margin
            const imageY = 5; // Top margin
            
            doc.addImage(
                imageUrl, 
                'PNG', 
                imageX + imagePadding, 
                imageY + imagePadding, 
                imageSize - (imagePadding * 2), 
                imageSize - (imagePadding * 2)
            );
        
        } catch (error) {
            console.log('Could not load image:', error);
        }

        let y = 15;
        let currentPage = 1;
    
        // Function to check if we need a new page
        const checkPageBreak = (requiredSpace = 20) => {
            if (y + requiredSpace > pageHeight - 20) {
                doc.addPage();
                y = 20;
                currentPage++;
                return true;
            }
            return false;
        };
    
        // Header with company name
        doc.setFont('Roboto', 'bold');
        doc.setFontSize(18);
        doc.text("MOLANTUR", 105, y, { align: "center" });
        y += 8;
        doc.setFontSize(14);
        doc.text("ATACAMA TOURS", 105, y, { align: "center" });
        y += 15;
    
        // Check page break after header
        checkPageBreak();
    
        // Reservation details table
        doc.setFont('Roboto', 'normal');
        doc.setFontSize(10);
        
        // Reservation number and date
        autoTable(doc, {
            startY: y,
            body: [
                [
                    { 
                        content: `Número de Reserva\n#${newGroupReservationData.id}`,
                        styles: { fontStyle: 'bold', halign: 'left' }
                    },
                    { 
                        content: `Fecha:\n${format(new Date(), 'dd/MM/yyyy')}`,
                        styles: { fontStyle: 'bold', halign: 'left' }
                    },
                    { 
                        content: `Cantidad de pasajeros\n${newGroup.length}`,
                        styles: { fontStyle: 'bold', halign: 'left' }
                    }
                ]
            ],
            theme: 'grid',
            styles: { fontSize: 10, cellPadding: 3 },
            columnStyles: {
                0: { cellWidth: 60 },
                1: { cellWidth: 50 },
                2: { cellWidth: 40 }
            },
            margin: { left: 20, right: 20 }
        });
        
        console.log('Reservation details added');
        y = (doc as any).lastAutoTable.finalY + 10;
    
        // Check page break before passengers table
        checkPageBreak(50);
    
        // Passengers table
        const passengers = newGroup.map((passenger, index) => {
            return [
                index + 1, 
                passenger.name || '', 
                passenger.rut_passport || '', 
                passenger.age ? `${passenger.age} y/o` : '', 
                passenger.phone || ''
            ];
        });
    
        console.log(passengers);
    
        autoTable(doc, {
            startY: y,
            head: [["#", "Nombre", "Documento", "EDAD", "CONTACTO"]],
            body: passengers,
            theme: "grid",
            styles: { fontSize: 9, cellPadding: 3 },
            headStyles: { 
                fillColor: [100, 100, 100], 
                textColor: 255, 
                fontStyle: 'bold',
                halign: "center" 
            },
            bodyStyles: { halign: "center" },
            margin: { left: 20, right: 20 },
            // Enable page breaks for long tables
            didDrawPage: function (data) {
                // Update y position after table is drawn
                y = (data.cursor?.y || y) + 10;
            }
        });
        
        y = (doc as any).lastAutoTable.finalY + 15;
    
        // Check page break before tour details
        checkPageBreak(100);
    
        // Tour details header
        doc.setFont('Roboto', 'bold');
        doc.setFontSize(12);
        doc.text("Detalles de la reserva", 20, y);
        y += 10;
    
        // Prepare tour details data
        const tourDetails = [];
        
        for (let i = 0; i < newGroupReservations.length; i++) {
            const reservation = newGroupReservations[i];
            const tourInfo = tours.find(tour => tour.tour_id === reservation.tour_id);
            
            tourDetails.push([
                i + 1,
                tourInfo?.tour_id || `Tour ${reservation.tour_id}`,
                reservation.reservation_date,
                tourInfo?.include?.join(', ') || 'No definido',
                tourInfo?.recommendations || tourInfo?.recommendations_es ||"Las recomendaciones que estan anotadas en cada tour",
                `$${reservation.price ? formatNumberToString(Number(reservation.price)) : '0'}`,
                reservation.tickets ? `$${formatNumberToString(Number(reservation.tickets))}` : '$0',
                `$${formatNumberToString(Number(reservation.price) + Number(reservation.tickets)) || '0'}`
            ]);
        }
    
        console.log(tourDetails);
    
        // Tour details table with adjusted column widths to fit page
        autoTable(doc, {
            startY: y,
            head: [["#", "Tour", "Fecha", "Incluido", "Recomendaciones", "Total Tour", "Total Tickets", "Total a pagar"]],
            body: tourDetails,
            theme: "grid",
            styles: { fontSize: 6, cellPadding: 2 }, // Smaller font for better fit
            headStyles: { 
                fillColor: [100, 100, 100], 
                textColor: 255, 
                fontStyle: 'bold',
                halign: "center",
                minCellHeight: 8,
                fontSize: 6
            },
            bodyStyles: { 
                halign: "center",
                minCellHeight: 8,
                fontSize: 6
            },
            // Adjusted column widths to fit page width
            columnStyles: {
                0: { cellWidth: 8 },
                1: { cellWidth: 30 }, // Reduced
                2: { cellWidth: 18 }, // Reduced
                3: { cellWidth: 30 }, // Reduced
                4: { cellWidth: 35 }, // Reduced
                5: { cellWidth: 25 }, // Reduced
                6: { cellWidth: 18 }, // Reduced
                7: { cellWidth: 18 }  // Reduced
            },
            margin: { left: 10, right: 10 }, // Reduced margins
            // Enable page breaks
            didDrawPage: function (data) {
                y = (data.cursor?.y || y) + 10;
            }
        });
        
        y = (doc as any).lastAutoTable.finalY + 10;
    
        // Check page break before totals
        checkPageBreak(30);
    
        // Calculate totals
        let totalTour = 0
        let totalTickets = 0
        for (let i = 0; i < newGroupReservations.length; i++) {
            const res = newGroupReservations[i];
            totalTour += Number(res.price)
            totalTickets += Number(res.tickets)

        }
        const totalPagar = totalTour + totalTickets;
        console.log(totalPagar)
    
        // FIXED: Totals table with proper width calculation
        const availableWidth = pageWidth - 30; // 15px margin on each side
        autoTable(doc, {
            startY: y,
            body: [
                [
                    { 
                        content: "TOTALES",
                        styles: { fontStyle: 'bold', halign: 'right' }
                    },
                    { 
                        content: `$${formatNumberToString(totalTour)}`,
                        styles: { fontStyle: 'bold', halign: 'center' }
                    },
                    { 
                        content: `$${formatNumberToString(totalTickets)}`,
                        styles: { fontStyle: 'bold', halign: 'center' }
                    },
                    { 
                        content: `$${formatNumberToString(totalPagar)}`,
                        styles: { fontStyle: 'bold', halign: 'center' }
                    }
                ]
            ],
            theme: 'grid',
            styles: { fontSize: 10, cellPadding: 5 },
            columnStyles: {
                0: { cellWidth: availableWidth * 0.6 }, // 60% for label
                1: { cellWidth: availableWidth * 0.13 }, // 13% for each value
                2: { cellWidth: availableWidth * 0.13 },
                3: { cellWidth: availableWidth * 0.13 }
            },
            margin: { left: 15, right: 15 }
        });
        
        y = (doc as any).lastAutoTable.finalY + 20;
    
        // Check page break before terms and conditions
        checkPageBreak(100);
    
        // Terms and conditions
        doc.setFont('Roboto', 'bold');
        doc.setFontSize(12);
        doc.text("Condiciones generales y políticas de cancelación", 20, y);
        y += 15;
    
        // Function to add text with automatic page breaks
        const addTextWithPageBreaks = (text: string, fontSize = 9, isBold = false) => {
            if (isBold) {
                doc.setFont('Roboto', 'bold');
            } else {
                doc.setFont('Roboto', 'normal');
            }
            doc.setFontSize(fontSize);
            
            const splitText = doc.splitTextToSize(text, pageWidth - 40);
            
            for (let i = 0; i < splitText.length; i++) {
                // Check if we need a new page for each line
                if (y > pageHeight - 15) {
                    doc.addPage();
                    y = 20;
                    currentPage++;
                }
                doc.text(splitText[i], 20, y);
                y += 5; // Line height
            }
            y += 5; // Extra space after paragraph
        };
    
        // Cancelation policy
        addTextWithPageBreaks("Cancelaciones y devoluciones:", 10, true);
        addTextWithPageBreaks("Con un periodo de 48 horas se puede cancelar con una devolución del 80% con menos de ese tiempo no se efectuará ninguna devolución. Sobre los cambios de itinerario pedimos las mismas 48 horas para realizar el cambio sin costo, sin embargo nos reservamos el derecho de aceptar o rechazar el cambio dependiendo nuestra disponibilidad.");
    
        // Responsibility exemption
        addTextWithPageBreaks("Exención de responsabilidad:", 10, true);
        addTextWithPageBreaks("Molantur se reserva el derecho de modificar itinerarios debido a condiciones climáticas o situaciones ajenas a nuestro control. No nos hacemos responsables por pérdidas personales o retrasos ocasionados por estas situaciones.");
    
        // Health and safety
        addTextWithPageBreaks("Salud y seguridad:", 10, true);
        addTextWithPageBreaks("Algunos tours se realizan por encima de los 4.000 metros sobre el nivel del mar. Se recomienda programar un período suficiente de aclimatación y realizar evaluación médica previa en caso de condiciones preexistentes ya que no se realizará devolución por temas médicos ni de falta de aclimatación a la altura.");
    
        // Restrictions
        addTextWithPageBreaks("Restricciones:", 10, true);
        addTextWithPageBreaks("Pasajeros bajo influencia de alcohol o drogas no podrán participar.");
    
        // Park closures
        addTextWithPageBreaks("Cierre imprevisto de parques o atracciones:", 10, true);
        addTextWithPageBreaks("Ante cierre imprevisto se cobrará hasta el 50 % del valor del tour para cubrir costos operativos.");
    
        // International tours documentation
        addTextWithPageBreaks("Documentación para tours internacionales (Uyuni):", 10, true);
        addTextWithPageBreaks("Es responsabilidad del pasajero verificar la documentación migratoria actualizada (pasaporte válido, permisos notariales para menores, visas, etc.). La falta de documentación adecuada implica la pérdida total del monto pagado.");
    
        // Insurance
        addTextWithPageBreaks("Seguro:", 10, true);
        addTextWithPageBreaks("Este servicio no incluye seguro de vida ni gastos médicos.");
    
        // Emergency contact
        addTextWithPageBreaks("Contacto de Emergencia:", 10, true);
        
        // Check page break before contact info
        checkPageBreak(20);
        
        doc.setFont('Roboto', 'normal');
        doc.setFontSize(10);
        doc.text("WhatsApp / Atención inmediata: +56 9 7577 4192", 25, y);
        y += 6;
        doc.text("Oficina Molantur: Pasaje Vilama 425-A, San Pedro de Atacama, Chile", 25, y);
        y += 15;
    
        // Final acceptance text
        checkPageBreak(15);
        doc.setFont('Roboto', 'normal');
        doc.setFontSize(9);
        const acceptanceText = "Al hacer uso de este voucher, el pasajero declara haber leído, comprendido y aceptado todas las condiciones aquí descritas.";
        addTextWithPageBreaks(acceptanceText);
    
        // Save PDF
        doc.save(`voucher_reserva_${newGroupReservationData.id || 'reserva'}.pdf`);
    }

     async function handleDownloadBoucher() {
        try {
            await printBoucher()
            setMessage({
                type: 'success',
                content: 'Boucher descargado correctamente'
            })
        } catch (error) {
            setMessage({
                type: 'error',
                content: 'Ocurrió un error, por favor intenta de nuevo'
            })
            console.log(error);
        } finally {
            setTimeout(() => {
                setMessage(undefined)
            }, 5000)
        }
    }
    
   

    const reservationsTotal = useMemo(() => {
        if (reservationData!.type === 'group') {
            return reducedReservations.map(r => Number(r.price) + Number(r.tickets)).reduce((a, b) => a + b, 0) 
        }
        return Number(reservationData!.price) + Number(reservationData!.tickets)
    }, [reducedReservations, reservationData])

    const totalPaid = useMemo(() => {
        return reservationData!.payments.map(p => Number(p.amount || 0) + Number( p.amount_tickets || 0)).reduce((a,b) => a + b, 0)
    }, [reservationData])
    return (
        <Modal>
            <button
            type="button"
            onClick={handleDownloadBoucher}
            >
                Download voucher PDF
            </button>
            {
            reservationData && reservationData.type === 'individual' && 
                <>
                <h1>Reservation Data</h1>
                
                <TableWrapper>
                    <Table>
                        <thead>
                            <th>Tour ID</th>
                            <th>Reservation Date</th>
                            <th>Total</th>
                        </thead>
                        <tbody>
                            <tr>
                                <td>{reservationData.tour_id}</td>
                                <td>{removeTimezone(reservationData.reservation_date)}</td>
                                <td>{reservationData.price + reservationData.tickets}</td>
                            </tr>
                        </tbody>
                    </Table>
                    <p>Total reserva: {reservationsTotal}</p>
                    <p>Total pagado: {totalPaid}</p>
                    <p>Total a pagar: {reservationsTotal - totalPaid}</p>
                </TableWrapper>
                </>
            }
            {
                reservationData!.type === 'individual' &&
                <>
                <h1>Passenger Data</h1>
                <TableWrapper>
                    <Table>
                        <thead>
                            <th>Passenger ID</th>
                            <th>Name</th>
                            <th>Email</th>
                            <th>Country</th>
                            <th>RUT/Passport</th>
                            <th>Hotel</th>
                            <th>Phone</th>
                            <th>Age</th>
                        </thead>
                        <tbody>
                            {
                                reservationData && (
                                    <tr>
                                        <td>{reservationData.passenger.id}</td>
                                        <td>{reservationData.passenger.name}</td>
                                        <td>{reservationData.passenger.email}</td>
                                        <td>{reservationData.passenger.nationality}</td>
                                        <td>{reservationData.passenger.rut_passport}</td>
                                        <td>{reservationData.passenger.hotel}</td>
                                        <td>{reservationData.passenger.phone}</td>
                                        <td>{reservationData.passenger.age}</td>
                                    </tr>
                                )
                            }
                        </tbody>
                    </Table>
                </TableWrapper>
                </>
            }
            {
                reservationData!.type === 'group' && 
                    <div>
                        <h1>
                            Group Reservations Data
                        </h1>
                        <TableWrapper>
                            <Table>
                                <thead>
                                    <th>Tour ID</th>
                                    <th>Date</th>
                                    <th>Total a pagar</th>
                                </thead>
                                <tbody>
                                    {
                                        reducedReservations.map((reservation) => (
                                            <tr key={reservation.id}>
                                                <td>{reservation.tour_id}</td>
                                                <td>{reservation.reservation_date}</td>
                                                <td>{Number(reservation.price) + Number(reservation.tickets)}</td>
                                            </tr>
                                        ))
                                    }
                                </tbody>
                            </Table>
                            <p>Total reserva: {reservationsTotal}</p>
                            <p>Total pagado: {totalPaid}</p>
                            <p>Total a pagar: {reservationsTotal - totalPaid}</p>
                        </TableWrapper>
                    </div>
            }
            {
                reservationData!.type === 'group' &&
                        <>
                        <h1>Passenger Data</h1>
                        <TableWrapper>
                            <Table>
                                <thead>
                                    <th>Passenger ID</th>
                                    <th>Name</th>
                                    <th>Email</th>
                                    <th>Country</th>
                                    <th>RUT/Passport</th>
                                    <th>Hotel</th>
                                    <th>Phone</th>
                                    <th>Age</th>
                                </thead>
                                <tbody>
                                    {
                                        reservationData!.reservations.map(r => r.passenger) 
                                        .filter((passenger, index, self) =>
                                            index === self.findIndex((p) => (
                                                p.id === passenger.id
                                            ))
                                        )
                                        .map((passenger) => { return (
                                            <tr>
                                                <td>{passenger.id}</td>
                                                <td>{passenger.name}</td>
                                                <td>{passenger.email}</td>
                                                <td>{passenger.nationality}</td>
                                                <td>{passenger.rut_passport}</td>
                                                <td>{passenger.hotel}</td>
                                                <td>{passenger.phone}</td>
                                                <td>{passenger.age}</td>
                                            </tr>
                                        )
                                    })
                                    }
                                </tbody>
                            </Table>
                        </TableWrapper>
                        </>
            }
            <button className="select"
            type="button"
            style={{ padding: '5px', cursor: 'pointer', margin: '10px'}}
            onClick={() => {
                if (reservationData!.type === 'group') {
                    setCreatePaymentGroupId(reservationData!.group_id)
                } else {
                    setCreatePaymentReservationId(reservationData!.id)
                }
                setViewPaymentsData(reservationData!.payments)
            }}
            >
                <BiListUl/>
            </button>
            <div className="close"
            onClick={() => setReservationData(undefined)}
            >
                <AiOutlineCloseCircle/>
            </div>
        </Modal>
    )
}

const TableWrapper = styled.div`
width: 100%;
overflow: auto;
min-height: 80px;
`

const Table = styled.table`
border-collapse: collapse;
position: relative;
table-layout: fixed;
height: 100%;
width: 100%;
min-width: 350px;
thead {
    width: 100%;
    background-color: #FFFFFF;
}
th {
    font-weight: 400;
    overflow: hidden;
    text-overflow: ellipsis;
}
td {
    text-align: center;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
    .flag {
        width: 30px;
    }
    button.select {
        width: 20px;
        height: 20px;
        color: #FFF;
        font-size: 18px;
        display: flex;
        align-items: center;
        justify-content: center;
        margin: 0 auto;
        line-height: 40px;
        background-color: ${props => props.theme.white};
        border: 1px solid ${props => props.theme.black};
        border-radius: 5px;
        cursor: pointer;
        transition: transform .3s ease;
        &:active {
            transform: scale(0.8);
        }
    }
    button.selected {
        background-color: ${props => props.theme.yellowDark};
    }
    button.info {
        border: none;
        background-color: none;
        font-size: 16px;
        cursor: pointer;
    }
}
tr {
    height: 40px;
    width: 100%;
}
`